import React, { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import * as authAPI from '../api/auth';
import { User } from '../api/auth';
import * as firebaseAuth from '../services/firebaseAuth';
import { isFirebaseEnabled } from '../config/firebase';

/**
 * 認證上下文類型定義
 */
export interface AuthContextType {
  // 認證狀態
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  token: string | null;
  authType: 'jwt' | 'firebase' | null; // 認證類型

  // JWT 認證操作（傳統方式）
  login: (email: string, password: string) => Promise<void>;
  register: (data: authAPI.RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  getCurrentUser: (firebaseIdToken?: string) => Promise<void>;

  // Firebase 認證操作
  signInWithGoogle: () => Promise<void>;
  signInWithFirebaseEmail: (email: string, password: string) => Promise<void>;
  registerWithFirebaseEmail: (email: string, password: string) => Promise<void>;

  // 共用功能
  isFirebaseEnabled: boolean;
}

/**
 * 認證上下文
 */
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * 認證提供者 Props
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * 認證提供者組件 (支援 Firebase + JWT 混合認證)
 *
 * 提供全域認證狀態管理：
 * - 自動從 localStorage 載入 token (JWT)
 * - 自動監聽 Firebase 認證狀態
 * - 提供 Google OAuth 登入
 * - 提供 Email/Password 登入 (Firebase + JWT)
 * - 處理認證錯誤 (自動清除無效 token)
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [authType, setAuthType] = useState<'jwt' | 'firebase' | null>(null);
  const firebaseEnabled = isFirebaseEnabled();

  /**
   * 初始化認證狀態
   * 暫時禁用 Firebase（2026-01-23），只使用 JWT
   */
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // 清除可能存在的 Firebase 相關數據（臨時措施）
        localStorage.removeItem('firebase_auth_type');
        localStorage.removeItem('firebase_token');

        // ========================================
        // 1. 暫時跳過 Firebase 檢查
        // ========================================
        if (false && firebaseEnabled) {
          const unsubscribe = firebaseAuth.onAuthStateChange(async (firebaseUser) => {
            if (firebaseUser) {
              try {
                // Firebase 使用者已登入，取得 ID Token
                const idToken = await firebaseAuth.getCurrentIdToken();

                if (idToken) {
                  // 使用 ID Token 向後端認證並取得本地使用者資訊
                  const response = await authAPI.getCurrentUser(idToken);

                  if (response.success && response.user) {
                    setUser(response.user);
                    setToken(idToken);
                    setAuthType('firebase');
                    setIsAuthenticated(true);
                    setIsLoading(false);
                    return;
                  }
                }
              } catch (error) {
                console.error('Firebase 認證處理失敗:', error);
              }
            }

            // Firebase 無使用者或認證失敗，檢查 JWT
            await checkJWTAuth();
            setIsLoading(false);
          });

          // 清理監聽器
          return () => unsubscribe();
        } else {
          // Firebase 未啟用，直接使用 JWT
          await checkJWTAuth();
          setIsLoading(false);
        }
      } catch (error) {
        console.error('認證初始化失敗:', error);
        await checkJWTAuth();
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [firebaseEnabled]);

  /**
   * 檢查 JWT 認證
   */
  const checkJWTAuth = async () => {
    try {
      const storedToken = localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('user');

      if (storedToken) {
        setToken(storedToken);

        // 先從 localStorage 讀取使用者資料（快速顯示）
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
            setAuthType('jwt');
            setIsAuthenticated(true);
          } catch (e) {
            console.error('解析 localStorage user 失敗:', e);
          }
        }

        // 背景驗證 token 並取得最新使用者資訊
        try {
          const response = await authAPI.getCurrentUser();

          if (response.success && response.user) {
            setUser(response.user);
            setAuthType('jwt');
            setIsAuthenticated(true);
          } else {
            // Token 無效，清除
            authAPI.clearAuthToken();
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
            setIsAuthenticated(false);
          }
        } catch (apiError) {
          console.error('API 驗證失敗，使用 localStorage 資料:', apiError);
          // API 失敗時，如果有 localStorage 資料就繼續使用
        }
      }
    } catch (error) {
      console.error('JWT 認證檢查失敗:', error);
      authAPI.clearAuthToken();
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  /**
   * 使用者登出（支援 Firebase + JWT）
   */
  const logout = useCallback(async () => {
    try {
      // 如果是 Firebase 認證，執行 Firebase 登出
      if (authType === 'firebase' && firebaseEnabled) {
        await firebaseAuth.logOut();
      }

      // 清除 JWT token
      authAPI.clearAuthToken();

      // 清除狀態
      setToken(null);
      setUser(null);
      setAuthType(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('登出失敗:', error);
      // 即使 Firebase 登出失敗，也清除本地狀態
      authAPI.clearAuthToken();
      setToken(null);
      setUser(null);
      setAuthType(null);
      setIsAuthenticated(false);
    }
  }, [authType, firebaseEnabled]);

  /**
   * 取得當前使用者資訊
   *
   * 暫時禁用 Firebase 支援（2026-01-23）
   * 只使用 JWT 認證方式
   */
  const getCurrentUser = useCallback(async () => {
    try {
      // 暫時強制使用 JWT 認證（不使用 Firebase）
      // 原因：後端 Firebase 端點尚未實作
      const response = await authAPI.getCurrentUser();

      if (response.success && response.user) {
        setUser(response.user);
        setIsAuthenticated(true);
      } else {
        throw new Error(response.message || '取得使用者資訊失敗');
      }
    } catch (error: any) {
      console.error('取得使用者資訊失敗:', error);
      // 如果是 401 錯誤，清除認證狀態
      if (error.response?.status === 401) {
        await logout();
      }
      throw error;
    }
  }, [logout]);

  // ========================================
  // JWT 認證操作（傳統方式）
  // ========================================

  /**
   * 使用者登入 (JWT)
   */
  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await authAPI.login({ email, password });

      if (response.success && response.token && response.user) {
        setToken(response.token);
        setUser(response.user);
        setAuthType('jwt');
        setIsAuthenticated(true);

        // Silently sign in to Firebase so auth.currentUser is available for email verification
        // on any browser/device, not just the one where registration happened.
        // Errors are intentionally ignored — Firebase auth is non-critical for JWT users.
        firebaseAuth.signInWithEmail(email, password).catch(() => undefined);
      } else {
        throw new Error(response.message || '登入失敗');
      }
    } catch (error: any) {
      console.error('登入失敗:', error);
      throw error;
    }
  }, []);

  /**
   * 使用者註冊 (JWT)
   */
  const register = useCallback(async (data: authAPI.RegisterRequest) => {
    try {
      const response = await authAPI.register(data);

      if (response.success && response.token && response.user) {
        setToken(response.token);
        setUser(response.user);
        setAuthType('jwt');
        setIsAuthenticated(true);
      } else {
        throw new Error(response.message || '註冊失敗');
      }
    } catch (error: any) {
      console.error('註冊失敗:', error);
      throw error;
    }
  }, []);

  // ========================================
  // Firebase 認證操作
  // ========================================

  /**
   * Google OAuth 登入
   */
  const signInWithGoogle = useCallback(async () => {
    if (!firebaseEnabled) {
      throw new Error('Firebase 認證未啟用');
    }

    try {
      const result = await firebaseAuth.signInWithGoogle();

      if (!result.success || !result.idToken) {
        throw new Error(result.error || 'Google 登入失敗');
      }

      // 使用 Firebase ID Token 向後端認證
      const response = await authAPI.getCurrentUser(result.idToken);

      if (response.success && response.user) {
        setUser(response.user);
        setToken(result.idToken);
        setAuthType('firebase');
        setIsAuthenticated(true);
      } else {
        throw new Error(response.message || '後端認證失敗');
      }
    } catch (error: any) {
      console.error('Google 登入失敗:', error);
      throw error;
    }
  }, [firebaseEnabled]);

  /**
   * Firebase Email/Password 登入
   */
  const signInWithFirebaseEmail = useCallback(
    async (email: string, password: string) => {
      if (!firebaseEnabled) {
        throw new Error('Firebase 認證未啟用');
      }

      try {
        const result = await firebaseAuth.signInWithEmail(email, password);

        if (!result.success || !result.idToken) {
          throw new Error(result.error || 'Email 登入失敗');
        }

        // 使用 Firebase ID Token 向後端認證
        const response = await authAPI.getCurrentUser(result.idToken);

        if (response.success && response.user) {
          setUser(response.user);
          setToken(result.idToken);
          setAuthType('firebase');
          setIsAuthenticated(true);
        } else {
          throw new Error(response.message || '後端認證失敗');
        }
      } catch (error: any) {
        console.error('Email 登入失敗:', error);
        throw error;
      }
    },
    [firebaseEnabled]
  );

  /**
   * Firebase Email/Password 註冊
   */
  const registerWithFirebaseEmail = useCallback(
    async (email: string, password: string) => {
      if (!firebaseEnabled) {
        throw new Error('Firebase 認證未啟用');
      }

      try {
        const result = await firebaseAuth.registerWithEmail(email, password);

        if (!result.success || !result.idToken) {
          throw new Error(result.error || 'Email 註冊失敗');
        }

        // 使用 Firebase ID Token 向後端認證（自動建立本地使用者）
        const response = await authAPI.getCurrentUser(result.idToken);

        if (response.success && response.user) {
          setUser(response.user);
          setToken(result.idToken);
          setAuthType('firebase');
          setIsAuthenticated(true);
        } else {
          throw new Error(response.message || '後端認證失敗');
        }
      } catch (error: any) {
        console.error('Email 註冊失敗:', error);
        throw error;
      }
    },
    [firebaseEnabled]
  );

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    token,
    authType,
    login,
    register,
    logout,
    getCurrentUser,
    signInWithGoogle,
    signInWithFirebaseEmail,
    registerWithFirebaseEmail,
    isFirebaseEnabled: firebaseEnabled,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
